export const DEPOSIT_HISTORY_PATH = "/user_profile/orders/deposit_history";
export const WITHDRAW_HISTORY_PATH = "/user_profile/orders/withdraw_history";
export const WITHDRAW_PAGE_PATH = "/asset_managemnet/withdraw";
export const WITHDRAW_AWAITING_PATH = "/asset_managemnet/withdraw?awaiting=1";

export const WALLET_DEPOSIT_UPDATED = "agce-wallet-deposit-updated";
export const WALLET_WITHDRAWAL_UPDATED = "agce-wallet-withdrawal-updated";
export const USER_WALLET_REFRESH = "agce-user-wallet-refresh";
export const DEPOSIT_HISTORY_REFRESH = "agce-deposit-history-refresh";
export const WITHDRAW_HISTORY_REFRESH = "agce-withdraw-history-refresh";

export function depositDeclarationPath(depositId) {
  return `${DEPOSIT_HISTORY_PATH}/${encodeURIComponent(String(depositId || "").trim())}`;
}

export function withdrawalDeclarationPath(withdrawalId) {
  return `${WITHDRAW_HISTORY_PATH}/${encodeURIComponent(String(withdrawalId || "").trim())}`;
}

function upper(value) {
  return String(value || "").trim().toUpperCase();
}

function declarationBlank(value) {
  return value == null || String(value).trim() === "";
}

/**
 * History row labels. Returns null when the pair is not one of the declaration cases,
 * so older status strings keep their existing label.
 */
export function depositRowPresentation(status, declarationStatus) {
  const s = upper(status);
  const d = declarationBlank(declarationStatus) ? "" : upper(declarationStatus);
  if (s === "PENDING" && d === "") {
    return { label: "Pending", tone: "pending", needsSenderConfirm: false };
  }
  if (s === "AWAITING_DECLARATION" && d === "PENDING") {
    return { label: "Provide Details", tone: "pending", needsSenderConfirm: true };
  }
  if (s === "COMPLETED" && (d === "SUBMITTED" || d === "")) {
    return { label: "Credited", tone: "success", needsSenderConfirm: false };
  }
  if (s === "REJECTED") {
    return { label: "Blocked", tone: "danger", needsSenderConfirm: false };
  }
  return null;
}

export function localDeclarationError(state) {
  if (state.senderIsSelf !== true && state.senderIsSelf !== false) {
    return "Say whether you sent this deposit";
  }
  if (state.sourceType !== "PRIVATE_WALLET" && state.sourceType !== "EXCHANGE") {
    return "Choose a private wallet or an exchange";
  }
  if (state.senderIsSelf === false && state.senderType !== "PERSONAL" && state.senderType !== "COMPANY") {
    return "Choose individual or company";
  }
  const personal = state.senderIsSelf === true || state.senderType === "PERSONAL";
  if (personal && (!String(state.firstName || "").trim() || !String(state.lastName || "").trim())) {
    return "First name and last name are required";
  }
  if (state.senderIsSelf === false && state.senderType === "COMPANY" && !String(state.companyName || "").trim()) {
    return "Company name is required";
  }
  if (!String(state.country || "").trim()) return "Country is required";
  if (state.sourceType === "EXCHANGE" && !String(state.exchangeName || "").trim()) {
    return "Exchange name is required";
  }
  if (state.confirmed !== true) return "Confirm that the information is true";
  return "";
}

/** Body contains only the fields that apply. The server stores PERSONAL when the sender is self. */
export function buildDeclarationBody(state) {
  const body = {
    sender_is_self: state.senderIsSelf,
    source_type: state.sourceType,
    country: String(state.country || "").trim(),
    confirmed: true,
  };
  if (state.senderIsSelf === false) {
    body.sender_type = state.senderType;
  }
  if (state.senderIsSelf === true || state.senderType === "PERSONAL") {
    body.first_name = String(state.firstName || "").trim();
    body.last_name = String(state.lastName || "").trim();
  }
  if (state.senderIsSelf === false && state.senderType === "COMPANY") {
    body.company_name = String(state.companyName || "").trim();
  }
  if (state.sourceType === "EXCHANGE") {
    body.exchange_name = String(state.exchangeName || "").trim();
  }
  return body;
}

export function classifyDeclarationSubmit(res) {
  if (res && res.success === true) {
    return { kind: "ok", message: res.message || "", data: res.data && typeof res.data === "object" ? res.data : {} };
  }
  const code = res?.code || "";
  if (code === "DECLARATION_INVALID") {
    return { kind: "invalid", message: res?.message || "" };
  }
  if (
    code === "DEPOSIT_NOT_FOUND" ||
    code === "WITHDRAWAL_NOT_FOUND" ||
    code === "DECLARATION_NOT_REQUIRED"
  ) {
    return { kind: "leave" };
  }
  return { kind: "retry" };
}

export function classifyDeclarationDetail(res) {
  if (res && res.success === true && res.data && res.data.deposit) {
    return { kind: "ok", data: res.data };
  }
  if (res?.code === "DEPOSIT_NOT_FOUND") return { kind: "leave" };
  return { kind: "error", message: res?.message || "Could not load this deposit." };
}

export function classifyWithdrawalDetail(res) {
  if (res && res.success === true && res.data && res.data.withdrawal) {
    return { kind: "ok", data: res.data };
  }
  if (res?.code === "WITHDRAWAL_NOT_FOUND") return { kind: "leave" };
  return { kind: "error", message: res?.message || "Could not load this withdrawal." };
}

export function parsePendingDeclarations(res) {
  if (!res || res.success === false) return { ok: false, count: 0, deposits: [] };
  const data = res.data && typeof res.data === "object" ? res.data : {};
  const deposits = Array.isArray(data.deposits) ? data.deposits : [];
  const n = typeof data.count === "string" ? Number(data.count) : data.count;
  const count = Number.isFinite(n) ? n : deposits.length;
  return { ok: true, count, deposits };
}

export function parsePendingWithdrawals(res) {
  if (!res || res.success === false) return { ok: false, count: 0, withdrawals: [] };
  const data = res.data && typeof res.data === "object" ? res.data : {};
  const withdrawals = Array.isArray(data.withdrawals) ? data.withdrawals : [];
  const n = typeof data.count === "string" ? Number(data.count) : data.count;
  const count = Number.isFinite(n) ? n : withdrawals.length;
  return { ok: true, count, withdrawals };
}

let countryOptionsCache = null;

/** 2-letter region codes. The API also accepts a country name. */
export function countrySelectOptions() {
  if (countryOptionsCache) return countryOptionsCache;
  const display = typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;
  const opts = [];
  if (display) {
    for (let i = 65; i <= 90; i += 1) {
      for (let j = 65; j <= 90; j += 1) {
        const code = String.fromCharCode(i) + String.fromCharCode(j);
        let name = "";
        try {
          name = display.of(code) || "";
        } catch {
          name = "";
        }
        if (!name || name.toUpperCase() === code) continue;
        if (opts.some((opt) => opt.label === name)) continue;
        opts.push({ value: code, label: name });
      }
    }
    opts.sort((a, b) => a.label.localeCompare(b.label));
  }
  countryOptionsCache = opts;
  return opts;
}

export function optionLabel(options, value) {
  const list = Array.isArray(options) ? options : [];
  const hit = list.find((opt) => opt && (opt.value === value || String(opt.value) === String(value)));
  if (hit && hit.label) return String(hit.label);
  if (value == null || value === "") return "";
  return String(value);
}
